import {
    Button,
    Card,
    Col,
    Descriptions,
    DescriptionsProps,
    Empty,
    Flex,
    Grid,
    Input,
    Row,
    Slider,
    Space,
    Switch,
    Tabs,
    TabsProps,
} from "antd";
import { LuPencil } from "react-icons/lu";
import { Text } from "theme/typography";
import { TYPOGRAPHY_SIZES } from "theme/constants";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { DraftProjectContext, ProjectContext } from "services/contexts";

import { IProject } from "models/project";
import { useAddProjectMutation, useGenerateCircuitMutation, useUpdateProjectNameMutation } from "../../redux/slices/project";
import Results from "./components/Results";
import EditProjectModal from "../EditProjectModal/EditProjectModal";
import Winding from "./components/Winding";

type InlineSvgProps = {
    svgText: string; // raw "<svg ...>...</svg>"
    className?: string;
    title?: string;
};

function InlineSvg({ svgText, className, title }: InlineSvgProps) {
    const mergedClassName = ["svg-inline", className].filter(Boolean).join(" ");
    return (
        <div
            className={mergedClassName}
            style={{ width: "100%" }}
            role="img"
            aria-label={title}
            // NOTE: Only do this with trusted SVG strings
            dangerouslySetInnerHTML={{ __html: svgText }}
        />
    );
}

interface IProps { }

type TabItem = NonNullable<TabsProps["items"]>[number];

const isTabItem = (item: TabItem | false | null | undefined): item is TabItem =>
    Boolean(item);

const formatValue = (value: number | string | undefined | null, sigFigs: number = 4): string => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (isNaN(num)) return "—";
  if (num === 0) return "0";
  const abs = Math.abs(num);
  if (abs < 0.01 || abs >= 10000) {
    return num.toExponential(sigFigs - 1);
  }
  return parseFloat(num.toPrecision(sigFigs)).toString();
};

const Project: FC<IProps> = () => {
    const { selectedProject } = useContext(ProjectContext);
    const { draftProject } = useContext(DraftProjectContext);

    const [project, setProject] = useState<IProject>();
    const [addProject, { isLoading: isLoadingAddProject }] = useAddProjectMutation();

    const [showCircuitWithData, setShowCircuitWithData] = useState<boolean>(false);
    const [circuitSliderValue, setCircuitSliderValue] = useState<number>(0.02);
    const [generateCircuit, { isLoading: isLoadingGenerateCircuit }] = useGenerateCircuitMutation();

    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [isEditingName, setIsEditingName] = useState<boolean>(false);
    const [editingNameValue, setEditingNameValue] = useState<string>("");
    const [updateProjectName, { isLoading: isUpdatingName }] = useUpdateProjectNameMutation();

    // Control active tab so we can safely switch if a tab disappears
    const [activeKey, setActiveKey] = useState<string>("1");
    const screens = Grid.useBreakpoint();

    useEffect(() => {
        // `name` puede venir vacío desde el backend (por ejemplo proyectos recién creados),
        // así que no debemos usarlo para decidir qué proyecto mostrar.
        // Priorizamos `selectedProject` (sidebar) para que el cambio siempre se refleje.
        if (selectedProject.id) setProject(selectedProject);
        else if (draftProject.id) setProject(draftProject);
        else setProject(undefined);
    }, [selectedProject, draftProject]);

    // If user is on "Equivalent Circuit" and topology changes, move them to a valid tab
    useEffect(() => {
        const hasEqCircuit = project?.topology === "Induction Machine";
        if (!hasEqCircuit && activeKey === "3") {
            setActiveKey("1");
        }
    }, [project?.topology, activeKey]);

    const onChangeCircuitVisualization = () => {
        setShowCircuitWithData((prev) => !prev);
    };

    const onGenerateCircuit = async () => {
        const circuit = await generateCircuit({
            slider_value: circuitSliderValue,
            stator_params: {},
        }).unwrap();

        if (circuit && project) {
            const r = { ...project.results, ec_with_data: circuit };
            setProject({ ...project, results: r });
        }
    };

    const itemsStatorData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const phaseResistance = resultsAny?.phase_resistance;
            const endWinding = resultsAny?.end_winding_inductance;
            const slotLeakage = resultsAny?.slot_leakage_inductance;
            const differentialHarmonicLeakageInductance = resultsAny?.differential_harmonic_leakage_inductance;
            const statorLeakageInductance = resultsAny?.stator_leakage_inductance;

            return [
                {
                    key: "1",
                    label: "Phase Resistance [Ω]",
                    children: formatValue(phaseResistance),
                },
                {
                    key: "2",
                    label: "End-winding inductance [H]",
                    children: formatValue(endWinding),
                },
                {
                    key: "3",
                    label: "Slot leakage inductance [H]",
                    children: formatValue(slotLeakage),
                },
                {
                    key: "4",
                    label: "Differential (harmonic) leakage inductance [H]",
                    children: formatValue(differentialHarmonicLeakageInductance),
                },
                {
                    key: "5",
                    label: "Stator leakage inductance [H]",
                    children: formatValue(statorLeakageInductance),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsRotorData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const barResistance = resultsAny?.bar_resistance;
            const rotorResistance = resultsAny?.rotor_resistance;
            const referredRotorResistance = resultsAny?.referred_rotor_resistance;
            const ringResistance = resultsAny?.ring_resistance;
            const endWinding = resultsAny?.rotor_end_winding_inductance;
            const slotLeakage = resultsAny?.rotor_slot_leakage_inductance;
            const differentialHarmonicLeakageInductance = resultsAny?.rotor_differential_harmonic_leakage_inductance;
            const rotorLeakageInductanceTotal = resultsAny?.rotor_leakage_inductance_total;
            const referredRotorLeakageInductance = resultsAny?.referred_rotor_leakage_inductance;

            return [
                {
                    key: "1",
                    label: "Bar resistance [Ω]",
                    children: formatValue(barResistance),
                },
                {
                    key: "2",
                    label: "Ring resistance [Ω]",
                    children: formatValue(ringResistance),
                },
                {
                    key: "3",
                    label: "Rotor resistance [Ω]",
                    children: formatValue(rotorResistance),
                },
                {
                    key: "4",
                    label: "Referred rotor resistance [Ω]",
                    children: formatValue(referredRotorResistance),
                },
                {
                    key: "5",
                    label: "End-winding inductance [H]",
                    children: formatValue(endWinding),
                    span: 2,
                },
                {
                    key: "6",
                    label: "Slot leakage inductance [H]",
                    children: formatValue(slotLeakage),
                    span: 2,
                },
                {
                    key: "7",
                    label: "Differential (harmonic) leakage inductance [H]",
                    children: formatValue(differentialHarmonicLeakageInductance),
                    span: 2,
                },
                {
                    key: "8",
                    label: "Rotor leakage inductance (Total) [H]",
                    children: formatValue(rotorLeakageInductanceTotal),
                    span: 2,
                },
                {
                    key: "9",
                    label: "Referred rotor leakage inductance [H]",
                    children: formatValue(referredRotorLeakageInductance),
                    span: 2,
                },
            ];
        },
        [project]
    );

    // Rotor variants (based on `project.params`)
    const itemsRotorTopBarSeparateRingData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const barResistanceTop = resultsAny?.bar_resistance_upper;
            const ringResistanceTop = resultsAny?.ring_resistance_upper;
            const endWindingTop = resultsAny?.rotor_end_winding_inductance_upper;
            const slotLeakageTop = resultsAny?.rotor_slot_leakage_inductance_upper;

            return [
                {
                    key: "1",
                    label: "Bar resistance [Ω]",
                    children: formatValue(barResistanceTop),
                },
                {
                    key: "2",
                    label: "Ring resistance [Ω]",
                    children: formatValue(ringResistanceTop),
                },
                {
                    key: "3",
                    label: "End-winding inductance [H]",
                    children: formatValue(endWindingTop),
                    span: 2,
                },
                {
                    key: "4",
                    label: "Slot leakage inductance [H]",
                    children: formatValue(slotLeakageTop),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsRotorBottomBarSeparateRingData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const barResistanceBottom = resultsAny?.bar_resistance_lower;
            const ringResistanceBottom = resultsAny?.ring_resistance_lower ;
            const endWindingBottom = resultsAny?.rotor_end_winding_inductance_lower;
            const slotLeakageBottom = resultsAny?.rotor_slot_leakage_inductance_lower;

            return [
                {
                    key: "1",
                    label: "Bar resistance [Ω]",
                    children: formatValue(barResistanceBottom),
                },
                {
                    key: "2",
                    label: "Ring resistance [Ω]",
                    children: formatValue(ringResistanceBottom),
                },
                {
                    key: "3",
                    label: "End-winding inductance [H]",
                    children: formatValue(endWindingBottom),
                    span: 2,
                },
                {
                    key: "4",
                    label: "Slot leakage inductance [H]",
                    children: formatValue(slotLeakageBottom),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsRotorSeparateRingData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const rotorResistance = resultsAny?.rotor_resistance;
            const referredRotorResistance = resultsAny?.referred_rotor_resistance;
            const differentialLeakage = resultsAny?.rotor_differential_harmonic_leakage_inductance;
            const rotorLeakageTotal = resultsAny?.rotor_leakage_inductance_total;
            const referredRotorLeakage = resultsAny?.referred_rotor_leakage_inductance;

            return [
                {
                    key: "1",
                    label: "Rotor resistance [Ω]",
                    children: formatValue(rotorResistance),
                },
                {
                    key: "2",
                    label: "Referred rotor resistance [Ω]",
                    children: formatValue(referredRotorResistance),
                },
                {
                    key: "3",
                    label: "Differential (harmonic) leakage inductance [H]",
                    children: formatValue(differentialLeakage),
                    span: 2,
                },
                {
                    key: "4",
                    label: "Rotor leakage inductance (Total) [H]",
                    children: formatValue(rotorLeakageTotal),
                    span: 2,
                },
                {
                    key: "5",
                    label: "Referred rotor leakage inductance [H]",
                    children: formatValue(referredRotorLeakage),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsRotorTopBarCommonRingData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const barResistanceTop = resultsAny?.bar_resistance_upper;
            const slotLeakageTop = resultsAny?.rotor_slot_leakage_inductance_upper;

            return [
                {
                    key: "1",
                    label: "Bar resistance [Ω]",
                    children: formatValue(barResistanceTop),
                },
                {
                    key: "2",
                    label: "Slot leakage inductance [H]",
                    children: formatValue(slotLeakageTop),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsRotorBottomBarCommonRingData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const barResistanceBottom = resultsAny?.bar_resistance_lower;
            const slotLeakageBottom = resultsAny?.rotor_slot_leakage_inductance_lower;

            return [
                {
                    key: "1",
                    label: "Bar resistance [Ω]",
                    children: formatValue(barResistanceBottom),
                },
                {
                    key: "2",
                    label: "Slot leakage inductance [H]",
                    children: formatValue(slotLeakageBottom),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsRotorCommonRingData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const ringResistance = resultsAny?.ring_resistance;
            const rotorResistance = resultsAny?.rotor_resistance;
            const referredRotorResistance = resultsAny?.referred_rotor_resistance;
            const endWinding = resultsAny?.rotor_end_winding_inductance;
            const differentialLeakage = resultsAny?.rotor_differential_harmonic_leakage_inductance;
            const rotorLeakageTotal = resultsAny?.rotor_leakage_inductance_total;
            const referredRotorLeakage = resultsAny?.referred_rotor_leakage_inductance;

            return [
                {
                    key: "1",
                    label: "Ring resistance [Ω]",
                    children: formatValue(ringResistance),
                },
                {
                    key: "2",
                    label: "Rotor resistance [Ω]",
                    children: formatValue(rotorResistance),
                },
                {
                    key: "3",
                    label: "Referred rotor resistance [Ω]",
                    children: formatValue(referredRotorResistance),
                },
                {
                    key: "4",
                    label: "End-winding inductance [H]",
                    children: formatValue(endWinding),
                    span: 2,
                },
                {
                    key: "5",
                    label: "Differential (harmonic) leakage inductance [H]",
                    children: formatValue(differentialLeakage),
                    span: 2,
                },
                {
                    key: "6",
                    label: "Rotor leakage inductance (Total) [H]",
                    children: formatValue(rotorLeakageTotal),
                    span: 2,
                },
                {
                    key: "7",
                    label: "Referred rotor leakage inductance [H]",
                    children: formatValue(referredRotorLeakage),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsRotorWoundFieldData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const rotorPhaseResistance = resultsAny?.rotor_phase_resistance;
            const referredRotorResistance = resultsAny?.referred_rotor_resistance;
            const endWinding = resultsAny?.rotor_end_winding_inductance;
            const slotLeakage = resultsAny?.rotor_slot_leakage_inductance;
            const differentialLeakage = resultsAny?.rotor_differential_harmonic_leakage_inductance;
            const rotorLeakageTotal = resultsAny?.rotor_leakage_inductance_total;
            const referredRotorLeakage = resultsAny?.referred_rotor_leakage_inductance;

            return [
                {
                    key: "1",
                    label: "Rotor phase resistance [Ω]",
                    children: formatValue(rotorPhaseResistance),
                },
                {
                    key: "2",
                    label: "Referred rotor resistance [Ω]",
                    children: formatValue(referredRotorResistance),
                },
                {
                    key: "3",
                    label: "End-winding inductance [H]",
                    children: formatValue(endWinding),
                    span: 2,
                },
                {
                    key: "4",
                    label: "Slot leakage inductance [H]",
                    children: formatValue(slotLeakage),
                    span: 2,
                },
                {
                    key: "5",
                    label: "Differential (harmonic) leakage inductance [H]",
                    children: formatValue(differentialLeakage),
                    span: 2,
                },
                {
                    key: "6",
                    label: "Rotor leakage inductance (Total) [H]",
                    children: formatValue(rotorLeakageTotal),
                    span: 2,
                },
                {
                    key: "7",
                    label: "Referred rotor leakage inductance [H]",
                    children: formatValue(referredRotorLeakage),
                    span: 2,
                },
            ];
        },
        [project]
    );

    const itemsMagnetizingBranchData: DescriptionsProps["items"] = useMemo(
        () => {
            const resultsAny = project?.results as any;
            const coreResistance = resultsAny?.core_resistance;
            const magnetizingInductance = resultsAny?.magnetizing_inductance;

            return [
                {
                    key: "1",
                    label: "Core Resistance [Ω]",
                    children: formatValue(coreResistance),
                },
                {
                    key: "2",
                    label: "Magnetizing Inductance [H]",
                    children: formatValue(magnetizingInductance),
                },
            ];
        },
        [project]
    );

    const items: TabsProps["items"] = useMemo(
        () =>
            [
                {
                    key: "1",
                    label: "Geometry",
                    children: (
                        <Row>
                            <Col span={12} xs={24} sm={12}>
                                <Space direction="vertical">
                                    <Text weight={600} size={TYPOGRAPHY_SIZES.H6}>
                                        Front View
                                    </Text>
                                    <InlineSvg svgText={(project?.results.geometry_front ?? "") as string} title="Front" />
                                </Space>
                            </Col>
                            <Col span={12} xs={24} sm={12}>
                                <Space direction="vertical">
                                    <Text weight={600} size={TYPOGRAPHY_SIZES.H6}>
                                        Side View
                                    </Text>
                                    <InlineSvg svgText={(project?.results.geometry_side ?? "") as string} title="Side" />
                                </Space>
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: "2",
                    label: "Winding",
                    children: <Winding topology="Induction Machine" project={project as IProject} />,
                },
                project?.topology === "Induction Machine" && {
                    key: "3",
                    label: "Equivalent Circuit",
                    children: (
                        <>
                            <Flex justify="end">
                                <Switch
                                    checkedChildren="Show Circuit without Data"
                                    unCheckedChildren="Show Circuit with Data"
                                    onChange={onChangeCircuitVisualization}
                                />
                            </Flex>

                            <Row>
                                {!showCircuitWithData ? (
                                    <Col span={12}>
                                        <Space direction="vertical">
                                            <Text weight={600} size={TYPOGRAPHY_SIZES.H6}>
                                                Without Data
                                            </Text>
                                            <InlineSvg svgText={(project?.results.ec_without_data ?? "") as string} title="Without" />
                                        </Space>
                                    </Col>
                                ) : (
                                    <Col span={12}>
                                        <Space direction="vertical">
                                            <Text weight={600} size={TYPOGRAPHY_SIZES.H6}>
                                                With Data
                                            </Text>
                                            <InlineSvg svgText={(project?.results.ec_with_data ?? "") as string} title="With Data" />
                                        </Space>

                                        <Flex justify="space-between">
                                            <Slider
                                                min={0}
                                                max={1}
                                                step={0.01}
                                                value={circuitSliderValue}
                                                style={{ width: "90%" }}
                                                onChange={(value) => setCircuitSliderValue(value as number)}
                                            />
                                            <Button
                                                type="primary"
                                                loading={isLoadingGenerateCircuit}
                                                onClick={onGenerateCircuit}
                                                disabled={circuitSliderValue === 0.02}
                                            >
                                                Apply
                                            </Button>
                                        </Flex>
                                    </Col>
                                )}
                            </Row>

                            <div
                                style={{
                                    columnCount: screens.xl ? 3 : screens.lg ? 2 : 1,
                                    columnGap: 16,
                                }}
                            >
                                <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                    <Descriptions title="Equivalent Circuit Data (Stator)" bordered items={itemsStatorData} column={1} />
                                </div>

                                {project?.params?.rotor_type === "Squirrel Cage" &&
                                    project?.params?.cage_type === "Single Cage" && (
                                        <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                            <Descriptions
                                                title="Equivalent Circuit Data (Rotor)"
                                                bordered
                                                items={itemsRotorData}
                                                column={1}
                                            />
                                        </div>
                                    )}

                                {project?.params?.rotor_type === "Squirrel Cage" &&
                                    project?.params?.cage_type === "Double Cage" &&
                                    project?.params?.ring_topology === "Separate Ring" && (
                                        <>
                                            <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                                <Descriptions
                                                    title="Equivalent Circuit Data (Rotor - Top Bar)"
                                                    bordered
                                                    items={itemsRotorTopBarSeparateRingData}
                                                    column={1}
                                                />
                                            </div>
                                            <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                                <Descriptions
                                                    title="Equivalent Circuit Data (Rotor - Bottom Bar)"
                                                    bordered
                                                    items={itemsRotorBottomBarSeparateRingData}
                                                    column={1}
                                                />
                                            </div>
                                            <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                                <Descriptions
                                                    title="Equivalent Circuit Data (Rotor)"
                                                    bordered
                                                    items={itemsRotorSeparateRingData}
                                                    column={1}
                                                />
                                            </div>
                                        </>
                                    )}

                                {project?.params?.rotor_type === "Squirrel Cage" &&
                                    project?.params?.cage_type === "Double Cage" &&
                                    project?.params?.ring_topology === "Common Ring" && (
                                        <>
                                            <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                                <Descriptions
                                                    title="Equivalent Circuit Data (Rotor - Top Bar)"
                                                    bordered
                                                    items={itemsRotorTopBarCommonRingData}
                                                    column={1}
                                                />
                                            </div>
                                            <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                                <Descriptions
                                                    title="Equivalent Circuit Data (Rotor - Bottom Bar)"
                                                    bordered
                                                    items={itemsRotorBottomBarCommonRingData}
                                                    column={1}
                                                />
                                            </div>
                                            <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                                <Descriptions
                                                    title="Equivalent Circuit Data (Rotor)"
                                                    bordered
                                                    items={itemsRotorCommonRingData}
                                                    column={1}
                                                />
                                            </div>
                                        </>
                                    )}

                                {project?.params?.rotor_type === "Wound Field" && (
                                    <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                        <Descriptions
                                            title="Equivalent Circuit Data (Rotor)"
                                            bordered
                                            items={itemsRotorWoundFieldData}
                                            column={1}
                                        />
                                    </div>
                                )}

                                <div style={{ breakInside: "avoid", marginBottom: 16 }}>
                                    <Descriptions
                                        title="Equivalent Circuit Data (Magnetizing Branch)"
                                        bordered
                                        items={itemsMagnetizingBranchData}
                                        column={1}
                                    />
                                </div>
                            </div>
                        </>
                    ),
                },
                {
                    key: "4",
                    label: "Results",
                    // Remount al cambiar el proyecto para limpiar cualquier estado interno del componente de charts.
                    children: (
                        <Results
                            key={project?.id || project?.created_at || project?.topology || "project"}
                            topology={project?.topology as string}
                            project={project as IProject}
                        />
                    ),
                },
            ].filter(isTabItem) as TabsProps["items"],
        [
            project,
            project?.topology,
            showCircuitWithData,
            circuitSliderValue,
            isLoadingGenerateCircuit,
            itemsStatorData,
            itemsRotorData,
            itemsRotorTopBarSeparateRingData,
            itemsRotorBottomBarSeparateRingData,
            itemsRotorSeparateRingData,
            itemsRotorTopBarCommonRingData,
            itemsRotorBottomBarCommonRingData,
            itemsRotorCommonRingData,
            itemsRotorWoundFieldData,
            itemsMagnetizingBranchData,
            screens.xl,
            screens.lg,
        ]
    );

    return (
        <>
            <EditProjectModal onClose={() => setOpenEditModal(false)} openModal={openEditModal} />

            <Card style={{ height: "94vh", overflowY: "auto" }}>
                {project && project?.topology !== "" ? (
                    <>
                        <Flex align="center" gap="small" style={{ marginBottom: 0 }}>
                            {isEditingName ? (
                                <Input
                                    value={editingNameValue}
                                    onChange={(e) => setEditingNameValue(e.target.value)}
                                    onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                                    onBlur={() => {
                                        const name = editingNameValue.trim();
                                        if (name && project?.id) {
                                            updateProjectName({ id: project.id, name })
                                                .unwrap()
                                                .then((updated) => {
                                                    setProject(updated);
                                                    setIsEditingName(false);
                                                });
                                        } else {
                                            setEditingNameValue(project?.name ?? "");
                                            setIsEditingName(false);
                                        }
                                    }}
                                    disabled={isUpdatingName}
                                    autoFocus
                                    style={{ maxWidth: 320 }}
                                />
                            ) : (
                                <>
                                    <Text weight={600} size={TYPOGRAPHY_SIZES.H5}>
                                        {project?.name ? project?.name : `[Draft] ${project?.topology}`}
                                    </Text>
                                    {project?.id && (
                                        <LuPencil
                                            size={18}
                                            style={{ cursor: "pointer", color: "#1677ff", flexShrink: 0 }}
                                            onClick={() => {
                                                setEditingNameValue(project?.name ?? "");
                                                setIsEditingName(true);
                                            }}
                                            aria-label="Edit project name"
                                        />
                                    )}
                                </>
                            )}
                        </Flex>

                        <Flex justify="end">
                            <Button
                                type="primary"
                                onClick={() => setOpenEditModal(true)}
                                disabled={project?.name === ""}
                                loading={isLoadingAddProject}
                            >
                                Edit
                            </Button>
                        </Flex>

                        <Tabs items={items} activeKey={activeKey} onChange={setActiveKey} />
                    </>
                ) : (
                    <Empty
                        image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                        styles={{ image: { height: 120 } }}
                        description={<Text size={TYPOGRAPHY_SIZES.H7}>Select or create a new project</Text>}
                    />
                )}
            </Card>
        </>
    );
};

export default Project;